import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { edgeFadeMask } from './edgeFadeMask'

/**
 * A horizontally-scrollable row that fades whichever edge still has hidden
 * overflow (see `edgeFadeMask`). Used by the floating chart toolbar so the chip
 * cluster hints at off-screen buttons when the panel is too narrow, instead of
 * cutting them off hard. Chip dropdowns portal to <body>, so the mask never
 * clips them.
 */
export function FadingScrollRow({
  className,
  children,
  fadePx,
}: {
  className?: string
  children: React.ReactNode
  fadePx?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [mask, setMask] = useState<string | undefined>(undefined)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    setMask(
      edgeFadeMask({
        scrollLeft: el.scrollLeft,
        clientWidth: el.clientWidth,
        scrollWidth: el.scrollWidth,
        fadePx,
      }),
    )
  }, [fadePx])

  // Re-measure after every commit (content added/removed changes scrollWidth).
  // Stable-value: setMask bails out when the mask string is unchanged, so this
  // settles in one extra pass and never loops.
  useLayoutEffect(() => {
    update()
  })

  // Track panel-width changes. ResizeObserver isn't in every test env — guard it.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => update())
    ro.observe(el)
    return () => ro.disconnect()
  }, [update])

  return (
    <div
      ref={ref}
      className={className}
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
      onScroll={update}
    >
      {children}
    </div>
  )
}
